/**
 * Dealer (代理店) 管理 API。
 *
 * 详见 docs/kaga-coupon/02-技術方案_加賀電子クーポン発行機能.md §4.2 §4.3
 */
import { Http } from '@mygpt/app/http';

// ============================================================
// Types
// ============================================================

export type CouponDuration = 'once' | 'forever' | 'repeating';
export type IssuedByRole = 'super_admin' | 'dealer_admin';

export interface DealerBrief {
  id: string;
  code: string;
  name: string;
  contact_email: string;
  is_active: boolean;
  created_at: string;
}

export interface DealerDetail extends DealerBrief {
  contact_phone?: string;
  bank_info?: Record<string, unknown>;
  incentive_rate: string; // Decimal serialized as string
}

export interface DealerAdminUserCreate {
  email: string;
  name: string;
  temp_password?: string;
}

export interface DealerCreateRequest {
  code: string;
  name: string;
  contact_email: string;
  contact_phone?: string;
  bank_info?: Record<string, unknown>;
  incentive_rate?: string;
  // v0.5: 二选一 —— 选既有用户 (user_id) OR 新建用户 (new)
  initial_admin_user_id?: string;
  initial_admin_new?: DealerAdminUserCreate;
}

export interface SearchableUser {
  id: string;
  email: string;
  name: string;
  dealer_id: string | null;
}

export const searchUsersForDealer = (params: { q?: string; limit?: number } = {}) => {
  const { get } = Http.use();
  return get<SearchableUser[]>('/dealers/users/search', { params });
};

export interface DealerUpdateRequest {
  name?: string;
  contact_email?: string;
  contact_phone?: string;
  bank_info?: Record<string, unknown>;
  incentive_rate?: string;
  is_active?: boolean;
}

export interface DealerCreateResponse {
  dealer: DealerDetail;
  initial_admin: {
    user: UserBrief;
    temp_password: string | null; // 选既有用户时为 null
  };
}

export interface UserBrief {
  id: string;
  email: string;
  name: string;
  created_at: string;
}

export interface PromoCodeCreate {
  code?: string;
  customer_email?: string;
  stripe_customer_id?: string;
  max_redemptions?: number;
  expires_at?: string;
  first_time_transaction?: boolean;
}

export interface CouponCreateRequest {
  name: string;
  percent_off?: number;
  amount_off?: number;
  currency?: string;
  duration?: CouponDuration;
  duration_in_months?: number;
  max_redemptions?: number;
  redeem_by?: string;
  reference_price_id?: string;
  promotion_code: PromoCodeCreate;
}

export interface PromoCodeDetail {
  id: string;
  code: string;
  customer_email: string | null;
  is_active: boolean;
  redeemed_count: number;
  max_redemptions: number;
  expires_at: string | null;
  qr_code_url: string;
  created_at: string;
  /** v0.9: 实际使用过该码的客户邮箱列表（按时间去重），与 customer_email 限制字段独立 */
  redeemed_by?: string[];
}

export interface CouponDetail {
  id: string;
  dealer_id: string;
  name: string;
  stripe_coupon_id: string;
  percent_off: number | null;
  amount_off: number | null;
  currency: string;
  duration: CouponDuration;
  is_active: boolean;
  issued_by_role: IssuedByRole;
  promotion_codes: PromoCodeDetail[];
  created_at: string;
}

export interface PageResp<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface MonthlyReportRow {
  customer_email: string;
  plan_name: string;
  coupon_code: string;
  standard_amount: number;
  discount_amount: number;
  net_amount: number;
  dealer_incentive_amount: number;
  billed_at: string;
  /** v0.9: 订阅创建日（契約日），展示用 */
  contract_date: string;
  /** v0.8: trial = 试用期 $0 invoice, paid = 实际扣费 */
  status?: 'trial' | 'paid';
}

/** v0.9: 月报查询条件 —— year/month 或 start_date/end_date 二选一，email/code 为附加过滤 */
export interface ReportQuery {
  year?: number;
  month?: number;
  customer_email?: string;
  code?: string;
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
}

export interface MonthlyReportResponse {
  dealer: DealerBrief;
  year: number;
  month: number;
  rows: MonthlyReportRow[];
  totals: Record<string, unknown>;
}

export interface DealerKpiResponse {
  this_month: {
    issued: number;
    contracts: number;
    incentive: number;
  };
}

// v0.9: 超管总览
export interface OverviewKpi {
  active_dealers: number;
  this_month_issued: number;
  this_month_contracts: number;
  this_month_incentive: number;
}

export interface OverviewDealerRow {
  id: string;
  code: string;
  name: string;
  contact_email: string;
  is_active: boolean;
  issued: number;
  contracts: number;
  incentive: number;
}

export interface OverviewResponse {
  kpi: OverviewKpi;
  dealers: OverviewDealerRow[];
  year: number;
  month: number;
}

export interface PromoLookupRow {
  id: string;
  code: string;
  customer_email: string | null;
  is_active: boolean;
  redeemed_count: number;
  max_redemptions: number;
  expires_at: string | null;
  created_at: string;
  redeemed_by: string[];
  dealer: DealerBrief;
}

// ============================================================
// Super Admin endpoints (/dealers/*)
// ============================================================

export const listDealers = (params: { page?: number; page_size?: number; search?: string; is_active?: boolean }) => {
  const { get } = Http.use();
  return get<PageResp<DealerBrief>>('/dealers', { params });
};

export const createDealer = (req: DealerCreateRequest) => {
  const { post } = Http.use();
  return post<DealerCreateResponse>('/dealers', req);
};

export const getDealer = (dealerId: string) => {
  const { get } = Http.use();
  return get<DealerDetail>(`/dealers/${dealerId}`);
};

export const updateDealer = (dealerId: string, req: DealerUpdateRequest) => {
  const { patch } = Http.use();
  return patch<DealerDetail>(`/dealers/${dealerId}`, req);
};

export const deactivateDealer = (dealerId: string) => {
  const http = Http.use();
  return http.delete(`/dealers/${dealerId}`);
};

export const listDealerAdminUsers = (dealerId: string) => {
  const { get } = Http.use();
  return get<UserBrief[]>(`/dealers/${dealerId}/users`);
};

export const addDealerAdminUser = (dealerId: string, req: { user_id: string }) => {
  const { post } = Http.use();
  return post<UserBrief>(`/dealers/${dealerId}/users`, req);
};

export const removeDealerAdminUser = (dealerId: string, userId: string) => {
  const http = Http.use();
  return http.delete(`/dealers/${dealerId}/users/${userId}`);
};

export const listCouponsSuper = (
  dealerId: string,
  params: { page?: number; page_size?: number; is_active?: boolean } = {}
) => {
  const { get } = Http.use();
  return get<PageResp<CouponDetail>>(`/dealers/${dealerId}/coupons`, { params });
};

export const createCouponSuper = (dealerId: string, req: CouponCreateRequest) => {
  const { post } = Http.use();
  return post<CouponDetail>(`/dealers/${dealerId}/coupons`, req);
};

export const deactivateCouponSuper = (couponId: string) => {
  const { patch } = Http.use();
  return patch<CouponDetail>(`/dealers/coupons/${couponId}`, {});
};

/** v0.9.1: 总览时间窗口 —— year+month 指定月，all=true 全期間累计，全部省略 = 当月 */
export interface OverviewQuery {
  year?: number;
  month?: number;
  all?: boolean;
}

export const getDealersOverview = (params: OverviewQuery = {}) => {
  const { get } = Http.use();
  return get<OverviewResponse>('/dealers/overview', { params });
};

/** v0.9.1: q 可省略 → 返回最近 N 条码（首屏预加载用） */
export const lookupPromotionCodes = (params: { q?: string; limit?: number } = {}) => {
  const { get } = Http.use();
  return get<PromoLookupRow[]>('/dealers/promotion-codes/lookup', { params });
};

export const deactivatePromoSuper = (promoId: string) => {
  const { patch } = Http.use();
  return patch<PromoCodeDetail>(`/dealers/promotion-codes/${promoId}`, {});
};

export const getMonthlyReportSuper = (dealerId: string, params: ReportQuery & { include_audit?: boolean }) => {
  const { get } = Http.use();
  return get<MonthlyReportResponse>(`/dealers/${dealerId}/reports/monthly`, { params });
};

const buildReportQuery = (params: ReportQuery & { format?: 'xlsx' | 'csv'; include_audit?: boolean }) => {
  const q = new URLSearchParams();
  if (params.year) q.set('year', String(params.year));
  if (params.month) q.set('month', String(params.month));
  if (params.customer_email) q.set('customer_email', params.customer_email);
  if (params.code) q.set('code', params.code);
  if (params.start_date) q.set('start_date', params.start_date);
  if (params.end_date) q.set('end_date', params.end_date);
  q.set('format', params.format || 'xlsx');
  if (params.include_audit) q.set('include_audit', 'true');
  return q.toString();
};

export const exportMonthlyReportSuperUrl = (
  dealerId: string,
  params: ReportQuery & { format?: 'xlsx' | 'csv'; include_audit?: boolean }
) => `/dealers/${dealerId}/reports/monthly/export?${buildReportQuery(params)}`;

// ============================================================
// Dealer Admin self-service endpoints (/dealers/me/*)
// ============================================================

export const getMyDealer = () => {
  const { get } = Http.use();
  return get<DealerDetail>('/dealers/me');
};

export const getMyKpi = () => {
  const { get } = Http.use();
  return get<DealerKpiResponse>('/dealers/me/kpi');
};

export const listMyCoupons = (
  params: {
    page?: number;
    page_size?: number;
    is_active?: boolean;
  } = {}
) => {
  const { get } = Http.use();
  return get<PageResp<CouponDetail>>('/dealers/me/coupons', { params });
};

export const createMyCoupon = (req: CouponCreateRequest) => {
  const { post } = Http.use();
  return post<CouponDetail>('/dealers/me/coupons', req);
};

export const deactivateMyCoupon = (couponId: string) => {
  const { patch } = Http.use();
  return patch<CouponDetail>(`/dealers/me/coupons/${couponId}`, {});
};

export const listMyPromotionCodes = (
  params: {
    page?: number;
    page_size?: number;
    status?: 'unused' | 'used' | 'expired';
  } = {}
) => {
  const { get } = Http.use();
  return get<PageResp<PromoCodeDetail>>('/dealers/me/promotion-codes', { params });
};

export const deactivateMyPromo = (promoId: string) => {
  const { patch } = Http.use();
  return patch<PromoCodeDetail>(`/dealers/me/promotion-codes/${promoId}`, {});
};

export const getMyMonthlyReport = (params: ReportQuery) => {
  const { get } = Http.use();
  return get<MonthlyReportResponse>('/dealers/me/reports/monthly', { params });
};

export const exportMyMonthlyReportUrl = (params: ReportQuery & { format?: 'xlsx' | 'csv' }) =>
  `/dealers/me/reports/monthly/export?${buildReportQuery(params)}`;

// ============================================================
// 测试用全局配置（仅 super_admin，dev/staging 使用）
// ============================================================

export interface DealerSystemSettings {
  /** Trial 时长 override（秒）。null 恢复默认「次月 1 日 00:00 JST」 */
  trial_end_offset_seconds: number | null;
}

export const getDealerSystemSettings = () => {
  const { get } = Http.use();
  return get<DealerSystemSettings>('/dealers/settings');
};

export const updateDealerSystemSettings = (req: DealerSystemSettings) => {
  const http = Http.use();
  return http.put<DealerSystemSettings>('/dealers/settings', req);
};
